import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
    User,
    Bookmark,
    Bell,
    Settings,
    Mail,
    LogOut,
} from "lucide-react";

interface AccountDropdownProps {
    onClose: () => void;
    user: any; // Using any for user to match existing usage, or could be User type if imported
}

export default function AccountDropdown({ onClose, user }: AccountDropdownProps) {
    const navigate = useNavigate();
    const { logout } = useAuth();
    const initial = user?.name?.charAt(0)?.toUpperCase() || "U";

    // Strict menu items list:
    // - My Profile
    // - Saved Opportunities
    // - Notifications
    // - Settings
    // - Contact Us
    // - Sign Out

    return (
        <div className="account-dropdown-container absolute right-0 top-full mt-3 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 py-4 z-50 pointer-events-auto max-h-[80vh] overflow-y-auto">
            {/* Header Section */}
            <div className="px-6 py-4 border-b border-gray-100">
                <div className="flex items-center space-x-3">
                    <div className="h-12 w-12 bg-blue-600 rounded-full flex items-center justify-center">
                        <span className="text-lg font-semibold text-white">{initial}</span>
                    </div>
                    <div className="overflow-hidden">
                        <p className="text-lg font-semibold text-gray-900 truncate">{user?.name || "User"}</p>
                        <p className="text-sm text-gray-500 truncate">{user?.email || "user@example.com"}</p>
                        <div className="flex items-center mt-1">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 border border-blue-200">
                                {(user?.profile_type || "Student")}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Menu Items */}
            <div className="py-3">
                <button
                    onClick={() => {
                        navigate("/my-profile");
                        onClose();
                    }}
                    className="flex w-full items-center px-6 py-3 text-base text-gray-700 hover:bg-gray-50 transition-colors"
                >
                    <User className="h-5 w-5 mr-4 text-gray-500" />
                    My Profile
                </button>

                <button
                    onClick={() => {
                        navigate("/saved-opportunities");
                        onClose();
                    }}
                    className="flex w-full items-center px-6 py-3 text-base text-gray-700 hover:bg-gray-50 transition-colors"
                >
                    <Bookmark className="h-5 w-5 mr-4 text-blue-500" />
                    Saved Opportunities
                </button>

                <button
                    onClick={() => {
                        navigate("/notifications");
                        onClose();
                    }}
                    className="flex w-full items-center px-6 py-3 text-base text-gray-700 hover:bg-gray-50 transition-colors"
                >
                    <Bell className="h-5 w-5 mr-4 text-teal-500" />
                    Notifications
                </button>

                <button
                    onClick={() => {
                        navigate("/settings");
                        onClose();
                    }}
                    className="flex w-full items-center px-6 py-3 text-base text-gray-700 hover:bg-gray-50 transition-colors"
                >
                    <Settings className="h-5 w-5 mr-4 text-gray-500" />
                    Settings
                </button>

                <button
                    onClick={() => {
                        navigate("/contact-us");
                        onClose();
                    }}
                    className="flex w-full items-center px-6 py-3 text-base text-gray-700 hover:bg-gray-50 transition-colors"
                >
                    <Mail className="h-5 w-5 mr-4 text-gray-500" />
                    Contact Us
                </button>
            </div>

            {/* Footer Section */}
            <div className="border-t border-gray-100 py-3">
                <button
                    onClick={() => {
                        logout();
                        navigate("/");
                        onClose();
                    }}
                    className="flex w-full items-center px-6 py-3 text-base text-red-600 hover:bg-red-50 transition-colors"
                >
                    <LogOut className="h-5 w-5 mr-4 text-red-500" />
                    Sign Out
                </button>
            </div>
        </div>
    );
}
